import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Typography,
  Box, IconButton, CircularProgress, Tooltip, Grid, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCodeIcon from '@mui/icons-material/QrCode';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import api from '../../api';
import CarnetSettingsDialog from './CarnetSettingsDialog';
import PageHeader from '../PageHeader';

export default function MuscuClasses() {
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [eleves, setEleves] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tokensByEleve, setTokensByEleve] = useState({});

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    setLoadingClasses(true);
    try {
      const res = await api.get('/classes');
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur chargement classes', err);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleSelectClasse = async (classeId) => {
    setSelectedClasse(classeId);
    setEleves([]);
    setTokensByEleve({});
    if (!classeId) return;

    setLoadingEleves(true);
    try {
      const res = await api.get(`/eleves?classe=${classeId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setEleves(list);
    } catch (err) {
      console.error('Erreur chargement élèves', err);
      setEleves([]);
    } finally {
      setLoadingEleves(false);
    }
  };

  const fetchTokenForEleve = async (eleve) => {
    try {
      const res = await api.post(`/muscu/tokens/eleve/${eleve._id}`);
      const { token } = res.data || {};
      if (!token) throw new Error('Token absent dans la réponse');
      const url = `${window.location.origin}/carnet-muscu/ajouter?seanceToken=${encodeURIComponent(token)}`;
      return { eleveId: eleve._id, token, url, ok: true };
    } catch (err) {
      console.error('Erreur génération token pour eleve', eleve._id, err);
      return { eleveId: eleve._id, ok: false, error: err };
    }
  };

  const generateTokensForList = async (list) => {
    if (!Array.isArray(list) || list.length === 0) return {};
    setGeneratingAll(true);
    const resultMap = { ...tokensByEleve };
    const batchSize = 8;

    for (let i = 0; i < list.length; i += batchSize) {
      const batch = list.slice(i, i + batchSize);
      const promises = batch.map((e) => {
        if (resultMap[e._id] && resultMap[e._id].token) {
          return Promise.resolve({
            eleveId: e._id,
            ok: true,
            token: resultMap[e._id].token,
            url: resultMap[e._id].url
          });
        }
        return fetchTokenForEleve(e);
      });

      const results = await Promise.all(promises);
      results.forEach((r) => {
        if (r.ok) {
          resultMap[r.eleveId] = { token: r.token, url: r.url, loading: false, error: false };
        } else {
          resultMap[r.eleveId] = { token: null, url: null, loading: false, error: true };
        }
      });

      setTokensByEleve({ ...resultMap });
    }

    setGeneratingAll(false);
    return resultMap;
  };

  const generateTokenForEleve = async (eleve) => {
    setTokensByEleve((prev) => ({ ...prev, [eleve._id]: { loading: true } }));
    const r = await fetchTokenForEleve(eleve);
    if (r.ok) {
      setTokensByEleve((prev) => ({
        ...prev,
        [eleve._id]: { token: r.token, url: r.url, loading: false, error: false }
      }));
    } else {
      setTokensByEleve((prev) => ({
        ...prev,
        [eleve._id]: { token: null, url: null, loading: false, error: true }
      }));
    }
  };

  const previewAllQr = async (preparedTokens = null) => {
    if (!eleves || eleves.length === 0) return;

    let snapshot = preparedTokens ? { ...preparedTokens } : { ...tokensByEleve };
    const missing = eleves.filter((e) => !(snapshot[e._id] && snapshot[e._id].token));

    if (missing.length > 0) {
      snapshot = await generateTokensForList(missing);
    }

    const cardsHtml = eleves.map((e) => {
      const entry = snapshot[e._id] || {};
      const url = entry.url || `${window.location.origin}/carnet-muscu/ajouter`;
      const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
      const name = `${e.prenom || ''} ${e.nom || ''}`;
      return `
        <div class="card">
          <h3>${escapeHtml(name)}</h3>
          <img src="${qrImg}" alt="QR" />
        </div>
      `;
    }).join('');

    const html = `
      <html>
        <head>
          <title>Prévisualisation QR - Carnet Muscu</title>
          <style>
            @media print { .no-print { display: none; } }
            body{font-family:Arial,Helvetica,sans-serif;padding:20px}
            .grid{display:flex;flex-wrap:wrap;gap:20px;justify-content:flex-start}
            .card{width:260px;border:1px solid #ddd;border-radius:8px;padding:12px;display:flex;flex-direction:column;align-items:center}
            .card h3{margin:8px 0;font-size:1em;text-align:center}
            .card img{width:220px;height:220px}
            .controls{margin-bottom:16px}
          </style>
        </head>
        <body>
          <div class="controls no-print">
            <button onclick="window.print()">Imprimer</button>
            <button onclick="window.close()">Fermer</button>
          </div>
          <div class="grid">${cardsHtml}</div>
        </body>
      </html>
    `;
    const w = window.open('', '_blank', 'width=1000,height=800');
    w.document.write(html);
    w.document.close();
  };

  const handleGenerateAllQr = async () => {
    if (!eleves || eleves.length === 0) return;
    const generatedMap = await generateTokensForList(eleves);
    await previewAllQr(generatedMap);
  };

  const escapeHtml = (str) => {
    if (!str) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
      '`': '&#x60;',
      '=': '&#x3D;'
    };
    return String(str).replace(new RegExp('[&<>"\'`=/]', 'g'), (s) => map[s]);
  };

  const selectedClasseName = classes.find((c) => c._id === selectedClasse)?.nom || '';

  return (
    <Box sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="LANCER LA SEANCE MUSCU"
        subtitle="Choisir une classe, générer les QR personnalisés pour donner accès aux carnets de muscu à vos élèves."
        backTo="/apps/carnet-muscu"
        homeTo="/"
        actionLabel="Réglages"
        onAction={() => setSettingsOpen(true)}
      />

      <Grid container spacing={{ xs: 1.25, sm: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
              background: 'linear-gradient(135deg, #fff4ea 0%, #fffaf5 100%)',
              border: '1px solid rgba(210,132,62,0.16)'
            }}
          >
            <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
              <Box
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  borderRadius: { xs: 2.5, sm: 3 },
                  background: 'linear-gradient(135deg, rgba(210,132,62,0.12) 0%, rgba(255,209,168,0.22) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                  flexShrink: 0
                }}
              >
                <GroupsRoundedIcon sx={{ fontSize: 30 }} />
              </Box>

              <Box sx={{ minWidth: 0, width: '100%' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    fontSize: { xs: '0.98rem', sm: '1.05rem' },
                    lineHeight: 1.2
                  }}
                >
                  Choisir une classe
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.84rem', sm: '0.92rem' },
                    lineHeight: 1.32,
                    mb: 1.5
                  }}
                >
                  Sélectionne une classe pour afficher les élèves et préparer leurs accès QR.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <select
                    value={selectedClasse}
                    onChange={(e) => handleSelectClasse(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: 8 }}
                  >
                    <option value="">-- Choisir --</option>
                    {loadingClasses
                      ? <option disabled>Chargement...</option>
                      : classes.map((c) => <option key={c._id} value={c._id}>{c.nom}</option>)}
                  </select>

                  <IconButton onClick={fetchClasses} title="Rafraîchir classes">
                    <RefreshIcon />
                  </IconButton>
                </Box>

                {selectedClasseName && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Classe sélectionnée : {selectedClasseName}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
              background: 'linear-gradient(135deg, #eef7ff 0%, #f8fbff 100%)',
              border: '1px solid rgba(51,120,220,0.16)'
            }}
          >
            <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
              <Box
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  borderRadius: { xs: 2.5, sm: 3 },
                  background: 'linear-gradient(135deg, rgba(51,120,220,0.12) 0%, rgba(156,205,255,0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                  flexShrink: 0
                }}
              >
                <QrCode2RoundedIcon sx={{ fontSize: 30 }} />
              </Box>

              <Box sx={{ minWidth: 0, width: '100%' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    fontSize: { xs: '0.98rem', sm: '1.05rem' },
                    lineHeight: 1.2
                  }}
                >
                  Générer les QR code élèves
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.84rem', sm: '0.92rem' },
                    lineHeight: 1.32,
                    mb: 1.5
                  }}
                >
                  Génère automatiquement les accès élèves puis ouvre directement la planche de prévisualisation prête à imprimer.
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleGenerateAllQr}
                  disabled={generatingAll || eleves.length === 0}
                >
                  GENERER LES QR CODE ELEVES
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
            border: '1px solid rgba(63,61,86,0.08)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
            Élèves {selectedClasseName ? `— ${selectedClasseName}` : ''}
          </Typography>

          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Prénom</TableCell>
                  <TableCell sx={{ width: 120 }}>QR code</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingEleves && (
                  <TableRow>
                    <TableCell colSpan={4} align="center"><CircularProgress /></TableCell>
                  </TableRow>
                )}

                {!loadingEleves && eleves.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>Aucun élève</TableCell>
                  </TableRow>
                )}

                {eleves.map((e) => {
                  const entry = tokensByEleve[e._id] || {};
                  const url = entry.url || `${window.location.origin}/carnet-muscu/ajouter`;
                  const qrImg = entry.token
                    ? `https://api.qrserver.com/v1/create-qr-code/?size=72x72&data=${encodeURIComponent(url)}`
                    : null;

                  return (
                    <TableRow key={e._id}>
                      <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.nom}</TableCell>
                      <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.prenom}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          {entry.loading ? (
                            <CircularProgress size={32} />
                          ) : entry.token && qrImg ? (
                            <>
                              <a href={url} target="_blank" rel="noopener noreferrer" title="Ouvrir la page élève" style={{ marginBottom: 6 }}>
                                <img src={qrImg} alt="QR" width={72} height={72} style={{ display: 'block' }} />
                              </a>
                              <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#3f51b5" }}>
                                Ouvrir la page élève
                              </a>
                            </>
                          ) : (
                            <Tooltip title="Générer QR">
                              <IconButton onClick={() => generateTokenForEleve(e)}><QrCodeIcon /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => {
                          if (entry && entry.url) window.open(entry.url, '_blank');
                          else generateTokenForEleve(e).then(() => {
                            const ent = tokensByEleve[e._id];
                            if (ent && ent.url) window.open(ent.url, '_blank');
                          });
                        }}>Ouvrir</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <CarnetSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
}